import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import { useToastStore } from '../../store/toast.store';
import * as service from './service';
import type {
  WithdrawalAccountDTO,
  WithdrawalAccountPayload,
  WithdrawalAccountStatusPayload,
} from './types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  'admin role required': '无管理员权限',
  network_error: '网络连接失败，请检查网络',
  'invalid user id': '用户 ID 无效',
  'invalid withdrawal account id': '提现账号 ID 无效',
  'withdrawal account name is required': '请输入姓名',
  'invalid withdrawal account name': '姓名格式不支持，请检查后重新输入',
  'withdrawal alipay account is required': '请输入支付宝账号',
  'withdrawal bank name is required': '请输入银行代码和银行名称',
  'withdrawal bank card number is required': '请输入银行卡号',
  'invalid withdrawal bank card number': '银行卡号格式不正确',
  'withdrawal account missing': '用户尚未绑定提现账号',
  'invalid withdrawal account type': '提现账号类型不支持',
  'withdrawal account not found': '提现账号不存在',
  'withdrawal account is locked': '该提现账号已有待处理提现，暂不可修改',
  'withdrawal account status invalid': '提现账号状态无效',
  'withdrawal alipay account already bound': '该支付宝账号已绑定其他用户',
  '该支付宝账号已绑定其他用户': '该支付宝账号已绑定其他用户',
};

function resolveError(error: unknown): string {
  if (isApiError(error)) return ERROR_MAP[error.message] ?? `请求失败 (${error.message})`;
  return '未知错误';
}

export function useWithdrawalAccounts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const addToast = useToastStore((s) => s.addToast);

  const marketId = searchParams.get('marketId') || 'CN';
  const userId = searchParams.get('userId') || '';
  const keyword = searchParams.get('keyword') || '';
  const accountKeyword = searchParams.get('accountKeyword') || '';
  const nameKeyword = searchParams.get('nameKeyword') || '';
  const status = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10) || 20;

  const requestSequence = useRef(0);
  const [items, setItems] = useState<WithdrawalAccountDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') next.set(key, value);
        else next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await service.listWithdrawalAccounts({
        marketId,
        page,
        pageSize,
        userId,
        keyword,
        accountKeyword,
        nameKeyword,
        status,
      });
      if (sequence !== requestSequence.current) return;
      setItems(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (e) {
      if (sequence === requestSequence.current) setError(resolveError(e));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [marketId, accountKeyword, keyword, nameKeyword, page, pageSize, status, userId]);

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);

  const setFilter = useCallback((name: string, value: string) => {
    updateParams({ [name]: value.trim() || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ pageSize: String(value), page: null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({ marketId }, { replace: true });
  }, [marketId, setSearchParams]);

  const saveAccount = useCallback(async (
    target: WithdrawalAccountDTO | { user_id: number },
    payload: WithdrawalAccountPayload,
  ): Promise<boolean> => {
    setSubmitting(true);
    try {
      if ('id' in target) {
        await service.updateWithdrawalAccount(target.id, payload);
      } else {
        await service.upsertUserAlipayWithdrawalAccount(target.user_id, payload);
      }
      addToast('success', '提现账号已保存');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, load]);

  const deleteAccount = useCallback(async (accountId: number): Promise<boolean> => {
    setSubmitting(true);
    try {
      await service.deleteWithdrawalAccount(accountId);
      addToast('success', '提现账号已删除');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, load]);

  const updateAccountStatus = useCallback(async (
    accountId: number,
    payload: WithdrawalAccountStatusPayload,
  ): Promise<boolean> => {
    setSubmitting(true);
    try {
      await service.updateWithdrawalAccountStatus(accountId, payload);
      addToast('success', payload.status === 'disabled' ? '提现账号已禁用' : '提现账号已启用');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [addToast, load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    marketId,
    items,
    total,
    page,
    pageSize,
    totalPages,
    userId,
    keyword,
    accountKeyword,
    nameKeyword,
    status,
    loading,
    error,
    submitting,
    setFilter,
    setPage,
    setPageSize,
    resetFilters,
    reload: load,
    saveAccount,
    deleteAccount,
    updateAccountStatus,
  };
}
